import { useFunctions, useObservable, useStorage } from 'reactfire'
import { Observable } from 'rxjs'
import { listAll, ref, uploadBytesResumable } from 'firebase/storage'
import { Button, Form, message, theme, Typography, Upload } from 'antd'
import { errorResponse } from '../../lib/errorResponse'
import { FilePdfOutlined } from '@ant-design/icons'
import { FileIcon } from './FileIcon'
import { getFileMetadata } from '../../lib/shared/getContentDisposition'
import { api } from '../../lib/api'
import { useTranslation } from 'react-i18next'

const fileListLoaders = {}

export function FileInputFormItem({
  name,
  parentPath,
  accept,
  required = true,
  maxCount,
  multiple = false,
  buttonText,
  setLoading,
  sessionToken,
}) {
  const storage = useStorage()
  const { t } = useTranslation()
  const { data: initialFileList } = useObservable(
    parentPath,
    new Observable(async (subscriber) => {
      async function loadFiles() {
        const parentRef = ref(storage, parentPath)
        const files = await listAll(parentRef)

        const filesWithMetadata = []

        let i = 0
        for (let entry of files.items) {
          const fileNameRegex = /filename\*=UTF-8''(.*)/
          const found = entry.name?.match(fileNameRegex)
          filesWithMetadata.push({
            uid: entry.name,
            name: found
              ? decodeURIComponent(found[1])
              : t('frontend:onboarding.fileFallbackName', { index: ++i }),
            path: entry.fullPath,
          })
        }

        subscriber.next(filesWithMetadata)
      }

      fileListLoaders[parentPath] = loadFiles

      await loadFiles()
    })
  )

  const getFile = (e) => {
    if (Array.isArray(e)) {
      return e
    }
    return e && e.fileList
  }

  const { token } = theme.useToken()

  return (
    <Form.Item
      name={name}
      valuePropName="fileList"
      rules={
        required
          ? [
              {
                required: true,
                message: t('frontend:onboarding.fieldRequired'),
              },
            ]
          : []
      }
      getValueFromEvent={getFile}
      style={{ marginTop: token.marginLG }}
      initialValue={initialFileList}
    >
      <FileInput
        parentPath={parentPath}
        accept={accept}
        multiple={multiple}
        maxCount={maxCount || 1}
        buttonText={buttonText}
        setLoading={setLoading}
        sessionToken={sessionToken}
      />
    </Form.Item>
  )
}

function FileInput({
  parentPath,
  accept,
  maxCount,
  multiple,
  buttonText,
  setLoading,
  sessionToken,
  ...props
}) {
  const { token } = theme.useToken()
  const storage = useStorage()
  const functions = useFunctions()
  const { t } = useTranslation()

  // Extract companyUid from parentPath (e.g., "onboarding/{companyUid}/payrollAccounts/")
  const companyUid = parentPath.split('/')[1]

  async function removeFile(file) {
    try {
      if (file.path) {
        // Use server-side API for secure deletion
        await api(functions, 'onboarding/delete_file', {
          companyUid,
          filePath: file.path,
          sessionToken,
        })
        fileListLoaders[parentPath]?.()
      }
    } catch (e) {
      errorResponse(e)
    }
  }

  const { status } = Form.Item.useStatus()
  const invalid = status === 'error'
  return (
    <Upload.Dragger
      {...props}
      style={{
        borderColor: invalid ? token.colorErrorText : undefined,
        color: invalid ? token.colorErrorText : undefined,
      }}
      accept={accept}
      multiple={multiple}
      showUploadList={true}
      iconRender={(file) => <FileIcon fileName={file.name} />}
      listType="picture"
      onRemove={removeFile}
      beforeUpload={(f) => {
        let canUpload = props.fileList.length < maxCount
        if (!canUpload) {
          if (maxCount === 1) {
            message.error(t('frontend:onboarding.maxOneFile'))
          } else {
            message.error(t('frontend:onboarding.maxFiles', { maxCount }))
          }
        }

        if (f.size === 0) {
          message.error(t('frontend:onboarding.fileEmpty'))
          canUpload = false
        }

        return canUpload || Upload.LIST_IGNORE
      }}
      customRequest={async ({ file, onProgress, onError, onSuccess }) => {
        setLoading(true)
        const fileRef = ref(
          storage,
          parentPath +
            file.uid +
            `;filename*=UTF-8''${encodeURIComponent(file.name)}`
        )
        const task = uploadBytesResumable(
          fileRef,
          file,
          getFileMetadata(file.name, file.type)
        )

        task.on('state_changed', (snapshot) => {
          onProgress({
            percent: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
          })
        })

        task.catch((e) => {
          onError(e)
        })

        task.then(() => {
          onSuccess()
          // Refresh the initial file list
          fileListLoaders[parentPath]?.()
          setLoading(false)
        })
      }}
    >
      <Button type="text" size="large" danger={invalid}>
        {buttonText ?? (
          <>
            <FilePdfOutlined /> {t('frontend:onboarding.chooseFile')}
          </>
        )}
      </Button>
      <Typography.Text
        type={invalid ? 'danger' : 'secondary'}
        style={{
          display: 'block',
          fontSize: token.fontSizeSM,
          marginTop: token.marginXS,
        }}
      >
        {accept &&
          t('frontend:onboarding.supportedFormats', {
            formats: accept.join(', '),
          })}{' '}
        {t('frontend:onboarding.maxCount', { maxCount })}
      </Typography.Text>
    </Upload.Dragger>
  )
}
